import { Box, Card, Divider, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';

interface DataTableCardSkeletonProps {
    /** Number of skeleton cards to render */
    count?: number;
    /** Whether to show a checkbox placeholder */
    checkboxSelection?: boolean;
    /** Number of label-value rows per card */
    rowCount?: number;
    /** Compact density */
    dense?: boolean;
}

const SingleCardSkeleton: React.FC<Pick<DataTableCardSkeletonProps, 'checkboxSelection' | 'rowCount' | 'dense'>> = ({
    checkboxSelection = false,
    rowCount = 4,
    dense = true,
}) => {
    const theme = useTheme();
    const px = dense ? 1.5 : 2;

    return (
        <Card
            elevation={0}
            sx={{
                position: 'relative',
                border: `1px solid ${theme.palette.border.main}`,
                borderRadius: 2,
                backgroundColor: theme.palette.background.paper,
                overflow: 'visible',
            }}
        >
            {/* Checkbox placeholder */}
            {checkboxSelection && (
                <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                    <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: '3px' }} />
                </Box>
            )}

            {/* Body rows */}
            <Box sx={{ px, pt: checkboxSelection ? 4 : dense ? 1 : 1.5, pb: 0 }}>
                {Array.from({ length: rowCount }).map((_, i) => (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            py: 0.5,
                        }}
                    >
                        {/* Label */}
                        <Skeleton variant="text" width={`${30 + (i % 3) * 10}%`} height={16} />
                        {/* Value */}
                        <Skeleton variant="text" width={`${20 + (i % 4) * 8}%`} height={16} />
                    </Box>
                ))}
            </Box>

            {/* Action area */}
            <Divider sx={{ mt: 0.75 }} />
            <Box sx={{ px, py: 0.75, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Skeleton variant="rounded" width={64} height={28} />
                <Skeleton variant="rounded" width={64} height={28} />
            </Box>
        </Card>
    );
};

export const DataTableCardSkeleton: React.FC<DataTableCardSkeletonProps> = ({
    count = 5,
    checkboxSelection = false,
    rowCount = 4,
    dense = true,
}) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {Array.from({ length: count }).map((_, i) => (
                <SingleCardSkeleton
                    key={i}
                    checkboxSelection={checkboxSelection}
                    rowCount={rowCount}
                    dense={dense}
                />
            ))}
        </Box>
    );
};

export default DataTableCardSkeleton;
